import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import apiHelper from 'helpers/api-helper';
import {schoolUiTexts} from 'data/ui-texts';
import { soundsData } from 'data/sound-data';
import { discussionQuestions } from 'data/discussion-data';
import Button from 'components/ui/button/button';
import Logo from 'components/ui/logo/logo';
import PopupGameStatusWarning from 'components/ui/popup-game-status-warning/popup-game-status-warning';
import './game-discussion.scss';

const GameDiscussion = (props) => {
	/* Props */
	const {
		game, 
		schoolData, 
		gameGroups, 
		handleGoToPage, 
		updateBackground, 
		scenarioId, 
		handlePlayMusic, 
		updateGame
	} = props;

	/* Track if show/hide warning popup */
	const [showStopGameWarningPopup, setShowStopGameWarningPopup] = useState(false);

	/* Track if calling backend to distribute questions */
	const [isDistributingQuestions, setIsDistributingQuestions] = useState(false);

	/* All available questions */
	const availableQuestions = schoolData.questions ? schoolData.questions : discussionQuestions;

	/* Selected questions */
	const selectedQuestions = [];
	if (game && game.selectedQuestions) {
		game.selectedQuestions.forEach((sq) => {
			const selectedQuestionIndex = selectedQuestions.findIndex((q) => {
				return q.questionId === sq.questionId;
			});
			if (selectedQuestionIndex < 0) {
				/* Add question to array of selected questions */
				const selectedQuestionObj = JSON.parse(JSON.stringify(sq));
				selectedQuestionObj.count = 1;
				selectedQuestions.push(selectedQuestionObj);
			} else {
				/* Increase counter (multiple groups have selected it) */
				selectedQuestions[selectedQuestionIndex].count += 1;
			}
		});
	}

	/* Even / odd number of questions */
	const isEvenNumberOfQuestions = selectedQuestions.length % 2 === 0;

	/* Groups that have not selected a question */
	const groupWithNoSelectedQuestions = gameGroups.filter((g) => {
		return (
			!selectedQuestions.some((q) => {return q.groupId === g.id;})
		);
	});

	/** 
	 * Navigates back to landing page and stops the music
	 * */ 
	const handleGoBack = () => {
		handlePlayMusic(false, null);
		updateGame({status: 'done'}).catch((error) => {
			console.error(error);
		});
	};

	/**
	 * Distribute questions randomly among groups that have not selected a question
	 */
	const distributeQuestionsRandomly = () => {
		if (isDistributingQuestions) return;
		setIsDistributingQuestions(true);
		apiHelper('school/distribute-questions-randomly', {
			gameId: game.id,
			defaultQuestions: discussionQuestions
		}).then((response) => {
			setIsDistributingQuestions(false);
			if (response.status === 'error') {
				console.error(response.error);
			}
		}).catch((error) => {
			setIsDistributingQuestions(false);
			console.error(error);
		});
	};

	/**
	 * Component did mount / will unmount
	 */
	useEffect(() => {
		// Set background image id
		updateBackground('school', scenarioId);
		const audioName = scenarioId + 'Music';
		handlePlayMusic(false, soundsData[audioName]);
		
		return () => {
			handlePlayMusic(false, null);
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);	

	return (
		<div className="GameDiscussion">
			{/* Logo */}
			<div className="GameDiscussion-logo" onClick={() => {handleGoToPage('landing');}} >
				<Logo scenarioId={scenarioId} />
			</div>

			{/* Selected questions */}
			<div className={'GameDiscussion-questions' + (isEvenNumberOfQuestions ? '' : ' odd')}>
				{selectedQuestions.map((q, index) => {
					const questionData = availableQuestions.find((dq) => {
						return dq.id === q.questionId;
					});
					if (!questionData) return null;
					return (
						<div key={questionData.id} className="GameDiscussion-questionWrap">
							<div className={'GameDiscussion-question ' + scenarioId}>
								<span>{questionData.text}</span>
							</div>
						</div>
					);
				})}
			</div>

			{/* Go to control panel btn */}
			<div className="GameDiscussion-controlPanelBtn">
				<Button 
					classes={['blueLight', game.scenarioId, 'responsive']}
					text={schoolUiTexts.goToControlPanel}
					onClick={() => {handleGoToPage('control-panel');}}
				/>
			</div>

			{/* Back to landing page btn */}
			<div className="GameDiscussion-backBtn2">
				<Button 
					classes={['blueLight', scenarioId, 'backBtn']}
					text={schoolUiTexts.back}
					onClick={() => {handleGoBack();}}
				/>
			</div>

			{/* Continue btn */}
			<div className="GameDiscussion-continueBtn">
				<Button 
					classes={['blueLight', game.scenarioId, 'responsive']}
					text={schoolUiTexts.gameDonePage.continueToEvaluation}
					onClick={() => {setShowStopGameWarningPopup(true);}}
				/>
			</div>



			{/* Distribute questions randomly btn */}
			{groupWithNoSelectedQuestions.length > 0 && <div className="GameDiscussion-distributeQuestionsBtn">
				<Button 
					isDisabled={isDistributingQuestions}
					isLoading={isDistributingQuestions}
					classes={['blueLight', game.scenarioId, 'responsive']}
					text={schoolUiTexts.distributeQuestionsRandomly}
					onClick={() => {distributeQuestionsRandomly();}}
				/>
			</div>}

			{/* Warning popup (confirm close game) */}
			{showStopGameWarningPopup &&
				<PopupGameStatusWarning 
					scenarioId={scenarioId}
					newStatus='evaluation'
					updateGame={updateGame}
					closePopup={() => {setShowStopGameWarningPopup(false);}}
				/>}
		</div>
	);
};

GameDiscussion.propTypes = {
	game: PropTypes.object.isRequired,
	schoolData: PropTypes.object.isRequired,
	gameGroups: PropTypes.array.isRequired,
	handleGoToPage: PropTypes.func.isRequired,
	updateBackground: PropTypes.func.isRequired,
	scenarioId: PropTypes.string.isRequired,
	handlePlayMusic: PropTypes.func.isRequired,
	updateGame: PropTypes.func.isRequired
};

export default GameDiscussion;
